import {
  enableSupportedWarehouseExperimentationManifests,
  enableWarehouseNativeMetricFilters,
  warehouseHostedMetricsSupportPercentiles,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import type { ExperimentDataSource, ExperimentType } from '@gonfalon/experiments/types';
import { ExperimentDataSourceKind, ExperimentTypeKind } from '@gonfalon/experiments/types';
import { MetricKind } from '@gonfalon/metrics/MetricKind';
import type { Metric } from '@gonfalon/metrics/types';

export const SNOWFLAKE_EXPERIMENTATION_MANIFEST_KEY = 'snowflake-experimentation';
export const DATABRICKS_EXPERIMENTATION_MANIFEST_KEY = 'databricks-experimentation';
export const REDSHIFT_EXPERIMENTATION_MANIFEST_KEY = 'redshift-experimentation';
export const BIGQUERY_EXPERIMENTATION_MANIFEST_KEY = 'bigquery-experimentation';
export const CLICKHOUSE_EXPERIMENTATION_MANIFEST_KEY = 'clickhouse-experimentation';
type WarehouseNativeExperimentType =
  | ExperimentTypeKind.SNOWFLAKE_NATIVE
  | ExperimentTypeKind.DATABRICKS_NATIVE
  | ExperimentTypeKind.REDSHIFT_NATIVE
  | ExperimentTypeKind.BIGQUERY_NATIVE
  | ExperimentTypeKind.CLICKHOUSE_NATIVE;

/**
 * The warehouse integration keys that are active when the feature flag
 * `enableSupportedWarehouseExperimentationManifests` returns an empty list.
 * Update this list when new warehouse types graduate out of feature-flag gating.
 */
export const DEFAULT_WAREHOUSE_MANIFEST_KEYS: string[] = [
  SNOWFLAKE_EXPERIMENTATION_MANIFEST_KEY,
  DATABRICKS_EXPERIMENTATION_MANIFEST_KEY,
];

/**
 * Maps experiment data source kinds to their corresponding warehouse integration keys.
 * Add new entries here when new warehouse types are introduced.
 */
export const EXPERIMENT_DATA_SOURCE_TO_INTEGRATION_KEY: Partial<Record<ExperimentDataSource, string>> = {
  [ExperimentDataSourceKind.SNOWFLAKE]: SNOWFLAKE_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentDataSourceKind.DATABRICKS]: DATABRICKS_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentDataSourceKind.REDSHIFT]: REDSHIFT_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentDataSourceKind.BIGQUERY]: BIGQUERY_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentDataSourceKind.CLICKHOUSE]: CLICKHOUSE_EXPERIMENTATION_MANIFEST_KEY,
};

/** Inverse of EXPERIMENT_DATA_SOURCE_TO_INTEGRATION_KEY for create-from-CAS. */
const INTEGRATION_KEY_TO_EXPERIMENT_DATA_SOURCE: Record<string, ExperimentDataSource> = Object.fromEntries(
  Object.entries(EXPERIMENT_DATA_SOURCE_TO_INTEGRATION_KEY).flatMap(([dataSource, key]) =>
    key === undefined ? [] : [[key, dataSource as ExperimentDataSource]],
  ),
);

export function experimentDataSourceFromIntegrationKey(integrationKey: string): ExperimentDataSource | undefined {
  return INTEGRATION_KEY_TO_EXPERIMENT_DATA_SOURCE[integrationKey];
}

/**
 * Maps experiment type kinds to their corresponding warehouse integration keys.
 * Used as a fallback when the experiment's dataSource is not yet set.
 * Add new entries here when new warehouse types are introduced.
 */
export const EXPERIMENT_TYPE_TO_INTEGRATION_KEY: Record<WarehouseNativeExperimentType, string> = {
  [ExperimentTypeKind.SNOWFLAKE_NATIVE]: SNOWFLAKE_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentTypeKind.DATABRICKS_NATIVE]: DATABRICKS_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentTypeKind.REDSHIFT_NATIVE]: REDSHIFT_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentTypeKind.BIGQUERY_NATIVE]: BIGQUERY_EXPERIMENTATION_MANIFEST_KEY,
  [ExperimentTypeKind.CLICKHOUSE_NATIVE]: CLICKHOUSE_EXPERIMENTATION_MANIFEST_KEY,
};

/**
 * Returns true when the experiment type corresponds to a warehouse-native experiment.
 * Derived from EXPERIMENT_TYPE_TO_INTEGRATION_KEY so adding a new warehouse type
 * automatically updates all callers.
 */
export function isWarehouseNativeExperimentType(
  experimentType: ExperimentType | undefined,
): experimentType is WarehouseNativeExperimentType {
  return experimentType !== undefined && experimentType in EXPERIMENT_TYPE_TO_INTEGRATION_KEY;
}

/**
 * Derives the expected warehouse integration key for an experiment.
 * Prefers the runtime dataSource value when available; falls back to
 * the static experimentType mapping when dataSource is temporarily unset
 * (e.g. during a form-type transition).
 */
export function getExpectedWarehouseIntegrationKey(
  experimentType: ExperimentType | undefined,
  dataSource: ExperimentDataSource | undefined,
): string | undefined {
  if (!isWarehouseNativeExperimentType(experimentType)) {
    return undefined;
  }
  if (dataSource) {
    return EXPERIMENT_DATA_SOURCE_TO_INTEGRATION_KEY[dataSource];
  }
  return EXPERIMENT_TYPE_TO_INTEGRATION_KEY[experimentType];
}

export const WAREHOUSE_METRIC_REQUIRED_MESSAGE =
  'Only metrics configured with a warehouse native data source can be used in warehouse native experiments';

export const WAREHOUSE_METRIC_ENV_MISMATCH_MESSAGE =
  'The data source for this metric was configured for a different environment';

export const WAREHOUSE_METRIC_SOURCE_MISMATCH_MESSAGE =
  'Only metrics configured with the same warehouse data source as this experiment can be used';

const RATIO_METRIC_NUMERATOR_SOURCE_MISMATCH_MESSAGE =
  'The numerator data source must match the experiment data source';

const RATIO_METRIC_NUMERATOR_ENV_MISMATCH_MESSAGE =
  'The numerator data source was configured for a different environment';

const RATIO_METRIC_DENOMINATOR_SOURCE_MISMATCH_MESSAGE =
  'The denominator data source must match the experiment data source';

const RATIO_METRIC_DENOMINATOR_ENV_MISMATCH_MESSAGE =
  'The denominator data source was configured for a different environment';

export function getWarehouseMetricUnavailableReason({
  metric,
  experimentEnvironmentKey,
  expectedWarehouseIntegrationKey,
  incompatibleMetricTypeMessage,
}: {
  metric: Metric;
  experimentEnvironmentKey?: string;
  expectedWarehouseIntegrationKey?: string;
  incompatibleMetricTypeMessage: string;
}): string | undefined {
  const allowedWarehouseIntegrationKeys = enableSupportedWarehouseExperimentationManifests();
  const metricIntegrationKey = metric.dataSource?._integrationKey;
  const isSupportedWarehouseMetric =
    metricIntegrationKey && allowedWarehouseIntegrationKeys.includes(metricIntegrationKey);

  if (!metric.dataSource || !isSupportedWarehouseMetric) {
    return WAREHOUSE_METRIC_REQUIRED_MESSAGE;
  }

  if (expectedWarehouseIntegrationKey && metricIntegrationKey !== expectedWarehouseIntegrationKey) {
    return metric.denominator
      ? RATIO_METRIC_NUMERATOR_SOURCE_MISMATCH_MESSAGE
      : WAREHOUSE_METRIC_SOURCE_MISMATCH_MESSAGE;
  }

  if (metric.denominator) {
    if (experimentEnvironmentKey && metric.dataSource.environmentKey !== experimentEnvironmentKey) {
      return RATIO_METRIC_NUMERATOR_ENV_MISMATCH_MESSAGE;
    }

    const denomIntegrationKey = metric.denominator.dataSource?._integrationKey;
    if (!denomIntegrationKey || !allowedWarehouseIntegrationKeys.includes(denomIntegrationKey)) {
      return RATIO_METRIC_DENOMINATOR_SOURCE_MISMATCH_MESSAGE;
    }
    if (expectedWarehouseIntegrationKey && denomIntegrationKey !== expectedWarehouseIntegrationKey) {
      return RATIO_METRIC_DENOMINATOR_SOURCE_MISMATCH_MESSAGE;
    }
    if (experimentEnvironmentKey && metric.denominator.dataSource?.environmentKey !== experimentEnvironmentKey) {
      return RATIO_METRIC_DENOMINATOR_ENV_MISMATCH_MESSAGE;
    }
  }

  if (experimentEnvironmentKey && metric.dataSource.environmentKey !== experimentEnvironmentKey) {
    return WAREHOUSE_METRIC_ENV_MISMATCH_MESSAGE;
  }

  // Filters only compile to SQL for Snowflake; every other warehouse fails at compute.
  const filtersUsable =
    enableWarehouseNativeMetricFilters() && metricIntegrationKey === SNOWFLAKE_EXPERIMENTATION_MANIFEST_KEY;

  if (
    metric.kind === MetricKind.CLICK ||
    metric.kind === MetricKind.PAGEVIEW ||
    (!!metric.filters && !filtersUsable) ||
    (!warehouseHostedMetricsSupportPercentiles() && metric.analysisType === 'percentile')
  ) {
    return incompatibleMetricTypeMessage;
  }

  return undefined;
}
